/**
 * Programa MVP 90 días — baseline vs resultado verificable.
 *
 * Hace que la plataforma "hable el lenguaje del MVP" del caso de
 * negocio: cada KPI declara su línea base medida, su meta y el valor
 * actual, con % de avance hacia la meta y método/fuente de medición.
 * Sin baseline defendible, el piloto no sirve — este módulo lo hace
 * explícito.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import { type MvpKpi, mvpProgress } from "../api/mock";
import { Icon } from "../components/Icon";
import { Sparkline } from "../components/Sparkline";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { exportRowsToCsv } from "../lib/exporters";

interface MvpProgram {
  perimetro: string;
  dma_incluidas: string[];
  semana_actual: number;
  semanas_total: number;
  fecha_inicio: string;
  kpis: MvpKpi[];
  hitos: Array<{
    semana: string;
    titulo: string;
    entregable: string;
    estado: "completado" | "en_progreso" | "pendiente";
  }>;
}

const CAT_LABEL: Record<MvpKpi["categoria"], string> = {
  operacional: "Operacional",
  financiero: "Financiero",
  datos: "Datos",
  red: "Red",
};

function fmtValue(k: MvpKpi, v: number): string {
  if (k.unidad === "CLP/mes") {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v.toFixed(0)}`;
  }
  if (k.unidad.startsWith("de ")) return `${v} ${k.unidad}`;
  const n = v % 1 === 0 ? v.toLocaleString("es-CL") : v.toFixed(1);
  return `${n} ${k.unidad}`;
}

function progressTone(pct: number): "accent" | "warn" | "danger" {
  if (pct >= 70) return "accent";
  if (pct >= 40) return "warn";
  return "danger";
}

export function MvpProgram(): JSX.Element {
  const { toast } = useToast();
  const q = useQuery<MvpProgram>({
    queryKey: ["mvp.program"],
    queryFn: () => api<MvpProgram>("/mvp/program"),
    staleTime: 60_000,
  });
  const p = q.data;

  const avgProgress = p
    ? Math.round(p.kpis.reduce((a, k) => a + mvpProgress(k), 0) / p.kpis.length)
    : 0;
  const onTrack = p ? p.kpis.filter((k) => mvpProgress(k) >= 60).length : 0;

  return (
    <>
      <PageHeader
        eyebrow="Programa"
        title="MVP 90 días · Baseline vs Resultado"
        subtitle="Cada KPI con línea base medida, meta y avance verificable. Sin baseline defendible, el piloto no cuenta."
        actions={
          <button
            className="btn"
            onClick={() => {
              const ok = exportRowsToCsv(
                "mvp-kpis-baseline.csv",
                (p?.kpis ?? []).map((k) => ({
                  kpi: k.nombre,
                  categoria: k.categoria,
                  unidad: k.unidad,
                  baseline: k.baseline,
                  meta: k.meta,
                  actual: k.actual,
                  avance_pct: mvpProgress(k),
                  metodo: k.metodo,
                  fuente: k.fuente,
                })),
              );
              toast(ok ? "KPIs del MVP exportados" : "Sin datos", ok ? "success" : "warn");
            }}
          >
            <Icon name="report" size={13} /> Exportar business case
          </button>
        }
      />

      {p && (
        <>
          <div className="card-grid" style={{ marginBottom: 16 }}>
            <div className="kpi kpi--info">
              <span className="kpi__label">Perímetro piloto</span>
              <span className="kpi__value" style={{ fontSize: 20 }}>{p.perimetro}</span>
              <span className="kpi__hint">{p.dma_incluidas.join(" · ")}</span>
            </div>
            <div className="kpi kpi--magenta">
              <span className="kpi__label">Semana</span>
              <span className="kpi__value">
                {p.semana_actual}/{p.semanas_total}
              </span>
              <span className="kpi__hint">inicio {p.fecha_inicio}</span>
            </div>
            <div className={`kpi kpi--${progressTone(avgProgress)}`}>
              <span className="kpi__label">Avance promedio a meta</span>
              <span className="kpi__value">{avgProgress} %</span>
              <span className="kpi__hint">{onTrack}/{p.kpis.length} KPIs on-track</span>
            </div>
            <div className="kpi kpi--accent">
              <span className="kpi__label">KPIs con baseline</span>
              <span className="kpi__value">{p.kpis.length}/{p.kpis.length}</span>
              <span className="kpi__hint">100% medibles y trazables</span>
            </div>
          </div>

          {/* KPIs baseline → meta */}
          <section className="card" style={{ marginBottom: 16 }}>
            <div className="card__title">
              <span>KPIs del piloto — baseline → actual → meta</span>
              <span className="text-faint text-mono" style={{ fontSize: 11 }}>
                verificable
              </span>
            </div>
            <div className="vstack" style={{ gap: 10 }}>
              {p.kpis.map((k) => {
                const pct = mvpProgress(k);
                return (
                  <div
                    key={k.kpi_id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.6fr 1fr 90px",
                      gap: 14,
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: "1px solid var(--c-divider)",
                    }}
                  >
                    <div>
                      <div className="hstack" style={{ gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--c-text-strong)" }}>
                          {k.nombre}
                        </span>
                        <span className="badge badge--neutral">{CAT_LABEL[k.categoria]}</span>
                      </div>
                      <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                        {k.metodo} · fuente:{" "}
                        <span className="text-strong">{k.fuente}</span>
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 11.5,
                          marginBottom: 4,
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        <span className="text-faint">base {fmtValue(k, k.baseline)}</span>
                        <span className="text-strong">{fmtValue(k, k.actual)}</span>
                        <span className="text-faint">meta {fmtValue(k, k.meta)}</span>
                      </div>
                      <div className="sla-bar">
                        <div
                          className="sla-bar__fill"
                          style={{
                            width: `${pct}%`,
                            background:
                              progressTone(pct) === "accent"
                                ? "var(--c-success)"
                                : progressTone(pct) === "warn"
                                  ? "var(--c-warn)"
                                  : "var(--c-danger)",
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <span
                        className={`badge badge--${progressTone(pct)}`}
                        style={{ fontSize: 12 }}
                      >
                        {pct}%
                      </span>
                      <div style={{ marginTop: 4 }}>
                        <Sparkline
                          values={k.serie}
                          height={22}
                          stroke={
                            progressTone(pct) === "accent"
                              ? "var(--c-success)"
                              : progressTone(pct) === "warn"
                                ? "var(--c-warn)"
                                : "var(--c-danger)"
                          }
                          fill="rgba(255,255,255,0.04)"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Hitos del MVP */}
          <section className="card">
            <div className="card__title">Plan de 12 semanas</div>
            <div style={{ position: "relative", paddingLeft: 16 }}>
              {p.hitos.map((h, idx) => (
                <div key={h.semana} style={{ position: "relative", paddingBottom: 14 }}>
                  <span
                    style={{
                      position: "absolute",
                      left: -16,
                      top: 3,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background:
                        h.estado === "completado"
                          ? "var(--c-success)"
                          : h.estado === "en_progreso"
                            ? "var(--c-warn)"
                            : "var(--c-surface-3)",
                      border: h.estado === "pendiente" ? "1px solid var(--c-border-strong)" : "none",
                    }}
                  />
                  {idx < p.hitos.length - 1 && (
                    <span
                      style={{
                        position: "absolute",
                        left: -12,
                        top: 13,
                        bottom: 0,
                        width: 1,
                        background: "var(--c-border)",
                      }}
                    />
                  )}
                  <div className="hstack" style={{ gap: 8 }}>
                    <span className="text-mono text-faint" style={{ fontSize: 11, minWidth: 38 }}>
                      S{h.semana}
                    </span>
                    <span style={{ fontSize: 13, color: "var(--c-text-strong)", fontWeight: 600 }}>
                      {h.titulo}
                    </span>
                    <span
                      className={`badge badge--${
                        h.estado === "completado" ? "accent" : h.estado === "en_progreso" ? "warn" : "neutral"
                      }`}
                    >
                      {h.estado.replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginLeft: 46 }}>
                    {h.entregable}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="status-block status-block--info" style={{ marginTop: 12 }}>
            <Icon name="shield" size={14} />
            Cada KPI se mide contra su línea base con método y fuente declarados. El
            <Link to="/impacto" style={{ margin: "0 4px" }}>business case</Link>
            se reconstruye a partir de estos valores verificables.
          </div>
        </>
      )}
    </>
  );
}
